<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarCheckbox,
  GrammarFooter,
  GrammarLocalLoader,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
} from '@nora-monorepo/grammar-ui';

import type { MessageState } from '#nora-chat-ui/states';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useForwardMessageStore } from '#nora-chat-ui/stores/forward-message-store';

interface ForwardMessageProps {
  messageState: MessageState;
}

const props = defineProps<ForwardMessageProps>();
const { t } = useI18n();
const conversationStore = useConversationStore();
const forwardMessageStore = useForwardMessageStore();

const { sortedConversation, getConversationTitle }
  = storeToRefs(conversationStore);
const { isSending } = storeToRefs(forwardMessageStore);

const selectedItems = ref<string[]>([]);

const showModal = computed(() => {
  return (
    forwardMessageStore.showForwardMessageModal
    && forwardMessageStore.messageId === props.messageState.message.id
  );
});

function toggleConversationSelection(conversationId: string) {
  if (selectedItems.value.includes(conversationId)) {
    selectedItems.value = selectedItems.value.filter(
      id => id !== conversationId,
    );
  }
  else {
    selectedItems.value.push(conversationId);
  }
}

function close() {
  forwardMessageStore.showForwardMessageModal = false;
}

async function confirm() {
  await forwardMessageStore.forwardMessages(selectedItems.value);

  selectedItems.value = [];
  forwardMessageStore.showForwardMessageModal = false;
}
</script>

<template>
  <GrammarModal
    :is-open="showModal"
    :label="t('forwardingMessage.forwardingMessagePopupTitle')"
    :show-close-button="false"
    @close="close"
  >
    <template #content>
      <div v-if="!isSending">
        <div
          v-for="(conversationState, index) in sortedConversation"
          :key="index"
          class="flex items-center mb-4 mt-3"
        >
          <GrammarCheckbox
            :checked="
              selectedItems.includes(
                conversationState.conversationListResult.id,
              )
            "
            :label="
              getConversationTitle(conversationState.conversationListResult.id)
            "
            @change="
              () =>
                toggleConversationSelection(
                  conversationState.conversationListResult.id,
                )
            "
          />
        </div>
      </div>
      <div v-else class="flex">
        <div class="m-auto">
          <GrammarLocalLoader />
        </div>
      </div>
    </template>

    <template #footer>
      <GrammarFooter>
        <template #left-button>
          <GrammarSecondaryButton :label="t('common.cancel')" @click="close" />
        </template>
        <template #right-button>
          <GrammarPrimaryButton :label="t('common.ok')" @click="confirm" />
        </template>
      </GrammarFooter>
    </template>
  </GrammarModal>
</template>
